import { TDocument } from '../documents/documents.interfaces';
import { TActions } from '../__shared/shared.interfaces';
import { EAcademicBoard } from './academicsClassesAndCourses.interfaces';

// Lesson Plan Status
export enum ELessonPlanStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

// Activity Types
export enum EActivityType {
  HOOK = 'hook', // Engaging introduction activity
  DISCUSSION = 'discussion',
  HANDS_ON = 'hands_on',
  DEMONSTRATION = 'demonstration',
  GROUP_WORK = 'group_work',
  INDIVIDUAL_WORK = 'individual_work',
  PRESENTATION = 'presentation',
  QUIZ = 'quiz',
  PRACTICAL = 'practical',
  FIELD_TRIP = 'field_trip',
  MULTIMEDIA = 'multimedia',
  OTHER = 'other',
}

// Multiple Intelligences (Gardner's Theory)
export enum EMultipleIntelligence {
  LINGUISTIC = 'linguistic',
  LOGICAL_MATHEMATICAL = 'logical_mathematical',
  SPATIAL = 'spatial',
  BODILY_KINESTHETIC = 'bodily_kinesthetic',
  MUSICAL = 'musical',
  INTERPERSONAL = 'interpersonal',
  INTRAPERSONAL = 'intrapersonal',
  NATURALISTIC = 'naturalistic',
}

// Special Care Needs (SCN) Categories
export enum ESCNCategory {
  LEARNING_DISABILITY = 'learning_disability',
  PHYSICAL_DISABILITY = 'physical_disability',
  VISUAL_IMPAIRMENT = 'visual_impairment',
  HEARING_IMPAIRMENT = 'hearing_impairment',
  AUTISM_SPECTRUM = 'autism_spectrum',
  ATTENTION_DEFICIT = 'attention_deficit',
  GIFTED_TALENTED = 'gifted_talented',
  SLOW_LEARNER = 'slow_learner',
  EMOTIONAL_BEHAVIORAL = 'emotional_behavioral',
  OTHER = 'other',
}

// Competency Types (CBSE/NCERT Framework)
export enum ECompetencyType {
  KNOWLEDGE = 'knowledge',
  UNDERSTANDING = 'understanding',
  APPLICATION = 'application',
  ANALYSIS = 'analysis',
  SYNTHESIS = 'synthesis',
  EVALUATION = 'evaluation',
  CREATION = 'creation',
  SKILL = 'skill',
  ATTITUDE = 'attitude',
  VALUE = 'value',
}

// Assessment Methods
export enum EAssessmentMethod {
  OBSERVATION = 'observation',
  ORAL_QUESTIONING = 'oral_questioning',
  WRITTEN_TEST = 'written_test',
  PRACTICAL = 'practical',
  PROJECT = 'project',
  PORTFOLIO = 'portfolio',
  SELF_ASSESSMENT = 'self_assessment',
  PEER_ASSESSMENT = 'peer_assessment',
  RUBRIC = 'rubric',
  CHECKLIST = 'checklist',
  OTHER = 'other',
}

// Learning Outcome Level (Bloom's Taxonomy / NCERT Framework)
export enum ELearningOutcomeLevel {
  REMEMBER = 'remember',
  UNDERSTAND = 'understand',
  APPLY = 'apply',
  ANALYZE = 'analyze',
  EVALUATE = 'evaluate',
  CREATE = 'create',
}

// Activity Interface
export interface ILessonActivity {
  id: string;
  type: EActivityType;
  title: string;
  description: string;
  duration: number; // in minutes
  materials?: string[]; // Required materials/resources (text references)
  documents?: TDocument[]; // Document attachments (PDFs, worksheets, etc.)
  instructions: string;
  expectedOutcome?: string;
  multipleIntelligences?: EMultipleIntelligence[]; // Which intelligences this targets
  scnAdaptations?: ISCNAdaptation[]; // Adaptations for special care needs
}

// Special Care Needs Adaptation
export interface ISCNAdaptation {
  scnCategory: ESCNCategory;
  adaptationDescription: string;
  modifiedMaterials?: string[]; // Text references to modified materials
  documents?: TDocument[]; // Document attachments for SCN adaptations
  modifiedInstructions?: string;
  additionalSupport?: string;
}

// Learning Example
export interface ILearningExample {
  id: string;
  title: string;
  description: string;
  context: string; // Real-world or contextual example
  relevance: string; // Why this example is relevant
  source?: string; // Reference source citation (text)
  documents?: TDocument[]; // Document attachments (images, PDFs, etc.)
}

// Learning Objective
export interface ILearningObjective {
  id: string;
  objective: string; // Clear, measurable objective
  competencyType: ECompetencyType;
  learningOutcomeLevel: ELearningOutcomeLevel;
  isPrimary: boolean; // Primary vs secondary objective
}

// Learning Outcome Mapping (NCERT/CBSE Framework)
export interface ILearningOutcome {
  id: string;
  code?: string; // NCERT/CBSE learning outcome code if applicable
  description: string;
  level: ELearningOutcomeLevel;
  competencyType: ECompetencyType;
  mappedToObjectiveIds: string[]; // Links to learning objectives
  boardSpecific?: {
    board: EAcademicBoard;
    code?: string; // Board-specific code
    description?: string; // Board-specific description
  }[];
}

// Competency-Based Assessment
export interface ICompetencyAssessment {
  id: string;
  competencyType: ECompetencyType;
  assessmentMethod: EAssessmentMethod;
  description: string;
  criteria: string; // Assessment criteria
  rubric?: {
    levels: string[]; // e.g., ['Excellent', 'Good', 'Satisfactory', 'Needs Improvement']
    descriptors: string[]; // Description for each level
  };
  documents?: TDocument[]; // Assessment documents (rubrics, worksheets, answer keys, etc.)
  weightage?: number; // Weightage in overall assessment
  duration?: number; // Time required for assessment
}

// Differentiation Strategy
export interface IDifferentiationStrategy {
  id: string;
  strategyType:
  | 'scn'
  | 'multiple_intelligence'
  | 'learning_style'
  | 'readiness_level'
  | 'interest';
  targetGroup?: ESCNCategory[] | EMultipleIntelligence[]; // Who this strategy targets
  description: string;
  implementation: string; // How to implement
  resources?: string[]; // Additional resources (text references)
  documents?: TDocument[]; // Document attachments for differentiation resources
}

// Session Details
export interface ILessonSession {
  id: string;
  sessionNumber: number; // Session number within the unit
  title: string;
  duration: number; // in minutes
  date?: string; // Scheduled date (optional for planning)
  objectives: string[]; // References to learning objective IDs
  activities: string[]; // References to activity IDs
  examples: string[]; // References to example IDs
  strategies: string; // Teaching strategies for this session
  closure: string; // Closure activity/reflection
  homework?: string;
  documents?: TDocument[]; // Session-specific documents (worksheets, handouts, etc.)
  notes?: string;
}

// Lesson Plan Unit/Block (15-day configurable block)
export interface ILessonPlanUnit {
  id: string;
  unitNumber: number;
  title: string;
  description?: string;
  duration: number; // Number of days (default 15, but configurable)
  startDate?: string;
  endDate?: string;
  sessions: ILessonSession[];
  learningObjectives: ILearningObjective[];
  learningOutcomes: ILearningOutcome[];
  assessments: ICompetencyAssessment[];
  differentiationStrategies: IDifferentiationStrategy[];
  resources?: string[]; // General resources (text references)
  documents?: TDocument[]; // Unit-level document attachments
  notes?: string;
}

// Main Lesson Plan Interface
export interface ILessonPlan {
  id: string;
  title: string;
  description?: string;

  // Academic Context
  academicYear: string;
  term?: string; // Term/Semester
  board: EAcademicBoard; // CBSE, ICSE, NCERT-aligned, etc.
  classId: number; // Reference to class
  courseId: number; // Reference to course/subject
  teacherId: number; // Teacher who created/teaches this

  // Curriculum Alignment
  curriculumFramework?: 'CBSE' | 'ICSE' | 'NCERT' | 'STATE_BOARD' | 'OTHER';
  syllabusReference?: string; // Reference to syllabus chapter/topic
  textbookReference?: string; // Textbook chapter/page reference
  ncertChapter?: string; // NCERT chapter reference if applicable

  // Unit Structure
  units: ILessonPlanUnit[];
  totalDuration: number; // Total days across all units

  // Shared Resources (across all units)
  activities: ILessonActivity[]; // Reusable activities
  examples: ILearningExample[]; // Reusable examples
  documents?: TDocument[]; // Lesson plan-level documents (overall plan PDF, templates, etc.)

  // Status and Metadata
  status: ELessonPlanStatus;
  version?: number; // Version number for revisions
  approvedBy?: number; // Teacher/Admin ID who approved
  approvedAt?: string;

  // Tags and Organization
  tags?: string[];
  subject?: string;
  gradeLevel?: string;

  // Timestamps
  actions: TActions;
}

// Response Interfaces for API
export interface ILessonPlanResponse {
  lessonPlans: ILessonPlan[];
  total: number;
}

export interface ILessonPlanDetailResponse {
  lessonPlan: ILessonPlan;
}

// ============================================================
// API Request/Response Interfaces (matching backend API)
// ============================================================

export interface ILessonPlanRequest {
  lessonPlan: {
    instituteId: string;
    title: string;
    description?: string;
    academicYear: string;
    term?: 'first' | 'second' | 'third';
    board?: string;
    classId?: string;
    courseId?: string;
    teacherId?: string;
    curriculumFramework?: string;
    syllabusReference?: string;
    textbookReference?: string;
    ncertChapter?: string;
    subject?: string;
    gradeLevel?: string;
    tags?: string[];
    status?: 'draft' | 'published' | 'archived';
    totalDuration?: number;
  };
}

export interface ILessonPlanApiResponse {
  id: string;
  title: string;
  description?: string;
  academicYear: string;
  term?: string;
  board?: string;
  class?: {
    id: string;
    name?: string;
    section?: string;
  };
  course?: {
    id: string;
    name?: string;
    subjectCode?: string;
  };
  teacher?: {
    id: string;
    name?: string;
  };
  curriculumFramework?: string;
  syllabusReference?: string;
  textbookReference?: string;
  ncertChapter?: string;
  subject?: string;
  gradeLevel?: string;
  tags?: string[];
  status: string;
  totalDuration?: number;
  version?: number;
  institute: {
    id: string;
    name?: string;
  };
  units?: ILessonPlanUnitApiResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface ILessonPlanUnitApiResponse {
  id: string;
  unitNumber: number;
  title: string;
  description?: string;
  duration?: number;
  objectives?: unknown;
  outcomes?: unknown;
  sessions?: ILessonPlanSessionApiResponse[];
}

export interface ILessonPlanSessionApiResponse {
  id: string;
  sessionNumber: number;
  title: string;
  description?: string;
  duration?: number;
  activities?: unknown;
}

export interface ILessonPlanListQueryParams {
  page?: number;
  limit?: number;
  instituteId?: string;
  classId?: string;
  courseId?: string;
  teacherId?: string;
  academicYear?: string;
  term?: string;
  status?: string;
  search?: string;
}

// Summary/Statistics Interface
export interface ILessonPlanSummary {
  totalLessonPlans: number;
  activeLessonPlans: number;
  draftLessonPlans: number;
  lessonPlansByBoard: {
    [key in EAcademicBoard]?: number;
  };
  lessonPlansByStatus: {
    [key in ELessonPlanStatus]?: number;
  };
  averageUnitDuration: number;
  totalUnits: number;
}

// Filter/Query Interface (legacy - use ILessonPlanListQueryParams for API)
export interface ILessonPlanFilters {
  academicYear?: string;
  term?: string;
  board?: EAcademicBoard;
  classId?: number;
  courseId?: number;
  teacherId?: number;
  status?: ELessonPlanStatus;
  curriculumFramework?: string;
  search?: string; // Search in title/description
}

